import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { calculationService, platformService, projectService, windDataService } from './api/client';
import {
  createDefaultCatalogueSelection,
  createDefaultGeometry,
  createNewProject,
  createUserDefinedMembrane,
  DEFAULT_ROOF_HEIGHT_MM,
  DEFAULT_ROOF_WIDTH_MM,
  USER_DEFINED_CHARACTERISTIC_RESISTANCE_KN,
  USER_DEFINED_FIXING_SYSTEM_NAME,
  USER_DEFINED_MEMBRANE_BRAND,
  USER_DEFINED_MEMBRANE_THICKNESS_MM,
  USER_DEFINED_MEMBRANE_TYPE,
  userDefinedFixingSystemId,
} from './app/defaultProject';
import {
  canBypassProductionReadiness,
  productionReadinessBlocksCalculation,
} from './app/developmentReadiness';
import { usePanelLayout, useTheme } from './app/preferences';
import { changedModuleId, undoBelongsToProject, type TrackedModules } from './app/projectHistory';
import { useHistory } from './shared/history';
import { isUsableVisualResult, selectVisualResult } from './app/visualState';
import { WorkspaceShell } from './app/WorkspaceShell';
import {
  CentreWorkspace,
  tabForShortcut,
  type VisualWorkspaceScope,
  type VisualWorkspaceTab,
} from './features/CentreWorkspace';
import { InputPanel, VIEW_LAYERS, type LayerVisibility } from './features/InputPanel';
import { DEFAULT_DRAWING_DISPLAY_SETTINGS } from './features/view/drawingDisplaySettings';
import { ReportDialog } from './features/report/ReportDialog';
import { downloadCalculationCsv } from './features/results/csvExport';
import type { CatalogueSelectionState } from './features/catalogue/CatalogueSelection';
import { ProjectDialog } from './features/project/ProjectDialog';
import {
  asUnsavedCopy,
  stateFromModule,
  stateFromProject,
} from './features/project/projectLifecycle';
import { ResultPanel } from './features/ResultPanel';
import { APPLICATION_VERSION } from './version';
import { fromContract, toContract, type GeometryModel } from './features/roof-plan-editor/model';
import type { CalculationResponse, Project, RoofModule, ValidationMessage } from './gen/ejot/roofing/v1/types';

const AUTO_CALCULATION_DEBOUNCE_MS = 350;
const NEW_ROOF_SITE_GAP_MM = 5_000;

export default function App() {
  const [theme, toggleTheme] = useTheme();
  const layout = usePanelLayout();
  const queryClient = useQueryClient();

  const [project, setProject] = useState<Project>(createNewProject);
  const moduleHistory = useHistory<TrackedModules>(project.modules);
  const [dirty, setDirty] = useState(false);
  const [layers, setLayers] = useState<LayerVisibility>(() =>
    Object.fromEntries(VIEW_LAYERS.map((layer) => [layer.id, true])));
  const [drawingDisplaySettings, setDrawingDisplaySettings] = useState(DEFAULT_DRAWING_DISPLAY_SETTINGS);
  const [geometry, setGeometry] = useState<GeometryModel>(createDefaultGeometry);
  const [activeModuleId, setActiveModuleId] = useState<string>('');
  const [catalogue, setCatalogue] = useState<CatalogueSelectionState>(createDefaultCatalogueSelection);
  const [reportOpen, setReportOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [bypassProductionReadiness, setBypassProductionReadiness] = useState(true);
  const [visualScope, setVisualScope] = useState<VisualWorkspaceScope>('roof');
  const [visualTab, setVisualTab] = useState<VisualWorkspaceTab>('viewer');
  const [selectedZoneId, setSelectedZoneId] = useState<string>();
  const [lastValidResult, setLastValidResult] = useState<CalculationResponse>();

  const capabilities = useQuery({
    queryKey: ['capabilities'],
    queryFn: ({ signal }) => platformService.getCapabilities({}, signal),
  });

  const nationalDatasets = useQuery({
    queryKey: ['national-wind-datasets'],
    queryFn: ({ signal }) => windDataService.listSelectableNationalDatasets({}, signal),
  });

  const readinessOverrideAvailable = canBypassProductionReadiness(capabilities.data);
  const readinessBlocksCalculation = productionReadinessBlocksCalculation(
    capabilities.data,
    bypassProductionReadiness,
  );
  const debouncedProject = useDebouncedValue(project, AUTO_CALCULATION_DEBOUNCE_MS);
  const calculationInputIsCurrent = debouncedProject === project;
  const calculationEnabled = !readinessBlocksCalculation && Boolean(debouncedProject.modules?.length);

  const calculation = useQuery({
    queryKey: ['project-calculation', debouncedProject],
    queryFn: ({ signal }) => calculationService.calculateProject({
      project: debouncedProject,
      request_token: `${debouncedProject.id || 'unsaved'}:${Date.now()}`,
    }, signal),
    enabled: calculationEnabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const saveProject = useMutation({
    mutationFn: async ({ saveAs }: { saveAs: boolean }) => {
      const payload = saveAs ? asUnsavedCopy(project) : project;
      if (!saveAs && payload.id) {
        return projectService.updateProject({ project: payload });
      }
      return projectService.createProject({ project: payload });
    },
    onSuccess: (response) => {
      if (response.project) {
        setProject(response.project);
        // Saving assigns an identity; it changes no engineering input, so it
        // updates the present without becoming a step the user can undo.
        moduleHistory.replace(response.project.modules);
      }
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const activeModule = project.modules?.find((module) => module.id === activeModuleId) ?? project.modules?.[0];
  const resolvedActiveModuleId = activeModule?.id ?? '';

  /**
   * Ctrl/Cmd+S saves the project rather than letting the browser offer to save
   * the page, which is never what someone means here. A save already in flight
   * is left alone so a repeated shortcut cannot queue duplicate writes.
   */
  const savePending = saveProject.isPending;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      if (savePending) return;
      saveProject.mutate({ saveAs: false });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [saveProject, savePending]);

  useEffect(() => {
    if (!resolvedActiveModuleId) return;
    if (activeModuleId !== resolvedActiveModuleId) setActiveModuleId(resolvedActiveModuleId);
  }, [activeModuleId, resolvedActiveModuleId]);

  /**
   * The one path every project change takes.
   *
   * A change that touches the modules is an engineering input and is recorded
   * as an undo step; a change to the project's identity alone is applied
   * without one, so the stack never holds a step that appears to do nothing.
   */
  const commitProject = useCallback((label: string, produce: (current: Project) => Project) => {
    const next = produce(project);
    if (next === project) return;
    setProject(next);
    setDirty(true);
    if (next.modules !== project.modules) moduleHistory.run(label, () => next.modules);
  }, [moduleHistory, project]);

  const updateProject = useCallback((next: Project) => {
    commitProject('project input', () => next);
  }, [commitProject]);

  const selectActiveModule = useCallback((moduleId: string) => {
    const module = project.modules?.find((candidate) => candidate.id === moduleId);
    if (!module) return;
    const restored = stateFromModule(module);
    setActiveModuleId(moduleId);
    setGeometry(restored.geometry);
    setCatalogue(restored.catalogue);
    setSelectedZoneId(undefined);
  }, [project.modules]);

  const openRoofWorkspace = useCallback((moduleId: string, preferredTab?: VisualWorkspaceTab) => {
    selectActiveModule(moduleId);
    setVisualScope('roof');
    if (preferredTab) setVisualTab(preferredTab);
  }, [selectActiveModule]);

  const selectRoofFromInput = useCallback((moduleId: string) => {
    selectActiveModule(moduleId);
    setVisualScope('roof');
  }, [selectActiveModule]);

  const addRoofModule = useCallback(() => {
    const existing = project.modules ?? [];
    const template = createNewProject().modules?.[0];
    if (!template) return;
    const sequence = existing.length + 1;
    const id = `module-${Date.now()}-${sequence}`;
    const siteOffsetX = (sequence - 1) * (DEFAULT_ROOF_WIDTH_MM + NEW_ROOF_SITE_GAP_MM);
    const templatePlan = template.build_up?.plan;
    const offsetPlan = templatePlan ? {
      ...templatePlan,
      revision: (templatePlan.revision ?? 0) + 1,
      outline: templatePlan.outline ? {
        ...templatePlan.outline,
        points: (templatePlan.outline.points ?? []).map((point) => ({
          ...point,
          x: (point.x ?? 0) + siteOffsetX,
        })),
      } : undefined,
    } : undefined;
    const next: RoofModule = {
      ...template,
      id,
      display_name: `Roof ${sequence}`,
      build_up: {
        ...template.build_up,
        plan: offsetPlan,
        section: template.build_up?.section ? { ...template.build_up.section } : undefined,
      },
      fixing_system: template.fixing_system ? { ...template.fixing_system } : undefined,
      internal_pressure: template.internal_pressure ? { ...template.internal_pressure } : undefined,
      zone_layouts: (template.zone_layouts ?? []).map((zoneLayout) => ({ ...zoneLayout })),
    };
    commitProject('add roof', (previous) => ({
      ...previous,
      modules: [...(previous.modules ?? []), next],
    }));
    setActiveModuleId(id);
    setGeometry(fromContract(next.build_up?.plan));
    setCatalogue(createDefaultCatalogueSelection());
    setSelectedZoneId(undefined);
    setLastValidResult(undefined);
    setVisualScope('roof');
    setVisualTab('viewer');
    setDirty(true);
  }, [commitProject, project.modules]);

  const deleteRoofModule = useCallback((moduleId: string) => {
    const modules = project.modules ?? [];
    if (modules.length <= 1) return;
    const removedIndex = modules.findIndex((module) => module.id === moduleId);
    if (removedIndex < 0) return;
    const remaining = modules.filter((module) => module.id !== moduleId);
    setProject((previous) => ({
      ...previous,
      modules: (previous.modules ?? []).filter((module) => module.id !== moduleId),
    }));

    if (moduleId === resolvedActiveModuleId) {
      const fallback = remaining[Math.min(removedIndex, remaining.length - 1)] ?? remaining[0];
      if (fallback?.id) {
        const restored = stateFromModule(fallback);
        setActiveModuleId(fallback.id);
        setGeometry(restored.geometry);
        setCatalogue(restored.catalogue);
      }
    }
    setSelectedZoneId(undefined);
    setLastValidResult(undefined);
    setDirty(true);
  }, [project.modules, resolvedActiveModuleId]);

  const updateGeometry = useCallback((next: GeometryModel) => {
    setGeometry(next);
    const targetId = activeModuleId || project.modules?.[0]?.id || '';
    const target = project.modules?.find((module) => module.id === targetId);
    if (target?.build_up?.plan?.revision === next.revision) return;
    commitProject('roof geometry', (previous) => ({
      ...previous,
      modules: (previous.modules ?? []).map((module) => (module.id === targetId
        ? { ...module, build_up: { ...module.build_up, plan: toContract(next) } }
        : module)),
    }));
  }, [activeModuleId, commitProject, project.modules]);

  const updateCatalogue = useCallback((next: CatalogueSelectionState) => {
    setCatalogue(next);
    const targetId = activeModuleId || project.modules?.[0]?.id || '';
    const etaMode = next.fixingBasis === 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';
    const userDefined = !etaMode && (next.completeSystemSource ?? 'USER_DEFINED') === 'USER_DEFINED';
    const brand = next.userDefinedMembraneBrand ?? USER_DEFINED_MEMBRANE_BRAND;
    const membraneType = next.userDefinedMembraneType ?? USER_DEFINED_MEMBRANE_TYPE;
    const thickness = next.userDefinedMembraneThicknessMm ?? USER_DEFINED_MEMBRANE_THICKNESS_MM;
    const fixingName = next.userDefinedFixingSystemName ?? USER_DEFINED_FIXING_SYSTEM_NAME;
    const selectedResistanceKn = next.userDefinedCharacteristicResistanceKn ?? USER_DEFINED_CHARACTERISTIC_RESISTANCE_KN;
    commitProject('product selection', (previous) => ({
      ...previous,
      modules: (previous.modules ?? []).map((module) => (module.id === targetId
        ? {
          ...module,
          build_up: {
            ...module.build_up,
            section: { ...module.build_up?.section, substrate_category: next.substrateCategory },
          },
          fixing_system: {
            ...module.fixing_system,
            membrane_product_id: userDefined ? undefined : next.membraneProductId,
            fixing_combination_id: userDefined
              ? userDefinedFixingSystemId(fixingName, selectedResistanceKn)
              : next.fixingCombinationId,
            eta_reference: next.etaReference,
            resistance_basis: next.fixingBasis,
            custom_membrane: userDefined
              ? createUserDefinedMembrane(brand, membraneType, thickness)
              : undefined,
          },
        }
        : module)),
    }));
  }, [activeModuleId, commitProject, project.modules]);

  const updateModule = useCallback((next: RoofModule) => {
    commitProject('module input', (previous) => ({
      ...previous,
      modules: (previous.modules ?? []).map((module) => (module.id === next.id ? next : module)),
    }));
  }, [commitProject]);

  const openPersistedProject = useCallback((loaded: Project) => {
    const restored = stateFromProject(loaded);
    const firstId = restored.project.modules?.[0]?.id ?? '';
    setProject(restored.project);
    // A different project starts a new history: its steps are not this one's.
    moduleHistory.reset(restored.project.modules);
    setActiveModuleId(firstId);
    setGeometry(restored.geometry);
    setCatalogue(restored.catalogue);
    setLastValidResult(undefined);
    setSelectedZoneId(undefined);
    setVisualScope('roof');
    setVisualTab('viewer');
    setDirty(false);
  }, [moduleHistory]);

  const createNew = useCallback(() => {
    const next = createNewProject();
    setProject(next);
    moduleHistory.reset(next.modules);
    setActiveModuleId(next.modules?.[0]?.id ?? '');
    setGeometry(createDefaultGeometry());
    setCatalogue(createDefaultCatalogueSelection());
    setLastValidResult(undefined);
    setSelectedZoneId(undefined);
    setVisualScope('roof');
    setVisualTab('viewer');
    setDirty(false);
  }, [moduleHistory]);

  /**
   * Restores a set of modules and follows it with the view.
   *
   * The mirrors the interface keeps of the active module — its geometry and
   * its catalogue selection — are rebuilt from the restored module, or they
   * would go on describing the state that was just taken back. The active
   * module follows the change so an undo is never invisible.
   */
  const applyRestoredModules = useCallback((restored: TrackedModules) => {
    const focusId = changedModuleId(project.modules, restored) ?? activeModuleId;
    const focus = restored?.find((module) => module.id === focusId) ?? restored?.[0];
    setProject((previous) => ({ ...previous, modules: restored }));
    setDirty(true);
    setSelectedZoneId(undefined);
    if (!focus) return;
    const mirrors = stateFromModule(focus);
    setActiveModuleId(focus.id ?? '');
    setGeometry(mirrors.geometry);
    setCatalogue(mirrors.catalogue);
  }, [activeModuleId, project.modules]);

  const undoProjectChange = useCallback(() => {
    if (!moduleHistory.canUndo) return;
    const restored = moduleHistory.undoModel;
    moduleHistory.undo();
    applyRestoredModules(restored);
  }, [applyRestoredModules, moduleHistory]);

  const redoProjectChange = useCallback(() => {
    if (!moduleHistory.canRedo) return;
    const restored = moduleHistory.redoModel;
    moduleHistory.redo();
    applyRestoredModules(restored);
  }, [applyRestoredModules, moduleHistory]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      const isUndo = key === 'z' && !event.shiftKey;
      const isRedo = (key === 'z' && event.shiftKey) || key === 'y';
      if (!isUndo && !isRedo) return;
      if (!undoBelongsToProject(event.target)) return;
      event.preventDefault();
      if (isUndo) undoProjectChange();
      else redoProjectChange();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [redoProjectChange, undoProjectChange]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const tab = tabForShortcut(event, visualScope);
      if (!tab) return;
      event.preventDefault();
      setVisualTab(tab);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visualScope]);

  const result = calculationEnabled && calculationInputIsCurrent ? calculation.data : undefined;
  const calculationIsUpdating = calculationEnabled
    && (!calculationInputIsCurrent || calculation.isFetching);
  const calculationError = calculationEnabled && calculationInputIsCurrent
    ? calculation.error
    : null;

  useEffect(() => {
    if (isUsableVisualResult(result)) setLastValidResult(result);
  }, [result]);

  const visualSelection = useMemo(
    () => selectVisualResult(result, lastValidResult),
    [result, lastValidResult],
  );

  const messages = useMemo<ValidationMessage[]>(() => {
    if (!result) return [];
    const collected: ValidationMessage[] = [...(result.messages ?? [])];
    for (const module of result.modules ?? []) {
      collected.push(...(module.messages ?? []));
      for (const zone of module.zones ?? []) collected.push(...(zone.messages ?? []));
    }
    return collected;
  }, [result]);

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-left">
          <span className="app-logo">EJOT</span>
          <span className="app-name">Roofing Insulation Calculator</span>
          <span className="app-version">v{APPLICATION_VERSION}</span>
          <span className="project-name">
            {project.metadata?.project_name || 'Untitled project'}
            {dirty && <span className="dirty-marker" title="Unsaved changes" aria-label="Unsaved changes">•</span>}
          </span>
        </div>

        <div className="app-header-right">
          {readinessOverrideAvailable && (
            <label
              className="header-readiness-override"
              title="Development only. This bypasses production-readiness UI gating, not backend engineering validity."
            >
              <input
                type="checkbox"
                checked={bypassProductionReadiness}
                onChange={(event) => setBypassProductionReadiness(event.target.checked)}
              />
              <span>Bypass production-readiness blocks</span>
            </label>
          )}
          <button
            type="button"
            className="header-button"
            disabled={!moduleHistory.canUndo}
            onClick={undoProjectChange}
            title={moduleHistory.undoLabel ? `Undo ${moduleHistory.undoLabel} (Ctrl+Z)` : 'Nothing to undo'}
          >
            Undo
          </button>
          <button
            type="button"
            className="header-button"
            disabled={!moduleHistory.canRedo}
            onClick={redoProjectChange}
            title={moduleHistory.redoLabel ? `Redo ${moduleHistory.redoLabel} (Ctrl+Shift+Z)` : 'Nothing to redo'}
          >
            Redo
          </button>
          <button type="button" className="header-button" onClick={createNew}>New</button>
          <button type="button" className="header-button" onClick={() => setProjectOpen(true)}>Open</button>
          <button
            type="button"
            className="header-button"
            disabled={saveProject.isPending}
            onClick={() => saveProject.mutate({ saveAs: false })}
            title="Save the project (Ctrl+S)"
          >
            {saveProject.isPending ? 'Saving' : 'Save'}
          </button>
          <button
            type="button"
            className="header-button"
            disabled={saveProject.isPending}
            onClick={() => saveProject.mutate({ saveAs: true })}
            title="Create a new persisted project identity from the current inputs"
          >
            Save As
          </button>
          <button
            type="button"
            className="header-button"
            disabled={!result}
            onClick={() => setReportOpen(true)}
            title={result ? 'Generate a report from the current authoritative result' : 'A current calculation result is required'}
          >
            Report
          </button>
          <button
            type="button"
            className="header-button"
            disabled={!result}
            onClick={() => result && downloadCalculationCsv(result, APPLICATION_VERSION)}
            title="Export the current authoritative backend result and quantity data"
          >
            Export CSV
          </button>

          <div className="panel-toggles" role="group" aria-label="Workspace panel visibility">
            <button
              type="button"
              className={layout.visible.left ? 'panel-toggle is-active' : 'panel-toggle'}
              aria-pressed={layout.visible.left}
              onClick={layout.toggleLeft}
            >
              Input
            </button>
            <button
              type="button"
              className={layout.visible.right ? 'panel-toggle is-active' : 'panel-toggle'}
              aria-pressed={layout.visible.right}
              onClick={layout.toggleRight}
            >
              Result
            </button>
            <button
              type="button"
              className="panel-toggle"
              onClick={layout.reset}
              title="Restore default panel widths and visibility"
            >
              Reset layout
            </button>
          </div>

          <button
            type="button"
            className="header-button"
            onClick={toggleTheme}
            aria-label={`Switch to the ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? 'Dark' : 'Light'}
          </button>

          <span className="status-chip" role="status" aria-live="polite">
            {autoCalculationLabel({
              blocked: readinessBlocksCalculation,
              updating: calculationIsUpdating,
              hasResult: Boolean(result),
              hasError: Boolean(calculationError),
            })}
          </span>
          <span className="status-chip" role="status" aria-live="polite">
            {connectionLabel(capabilities)}
          </span>
        </div>
      </header>

      {saveProject.isError && (
        <div className="global-notice global-notice-blocked" role="alert">
          The project could not be saved. No saved-state claim was made: {String(saveProject.error)}
        </div>
      )}

      {capabilities.data?.development_catalogue_active && (
        bypassProductionReadiness && readinessOverrideAvailable ? (
          <div className="global-notice" role="note" aria-label="Development readiness override status">
            <strong>Development override active.</strong>{' '}
            Production-readiness UI blocks are temporarily bypassed for software verification. The backend development
            engine still enforces geometry, required inputs, supported scope, catalogue consistency, resistance validity,
            and calculation PASS/FAIL rules. Results remain development-only and are not approved for production design.
          </div>
        ) : (
          <div className="global-notice global-notice-blocked" role="alert" aria-label="Production engineering status">
            <strong>Production engineering blocked.</strong>{' '}
            The development override is off. Automatic calculation is paused while production engineering approvals remain
            open. Development engineering mode exists for software verification, but it is not a production design approval.
          </div>
        )
      )}

      <ProjectDialog open={projectOpen} onClose={() => setProjectOpen(false)} onOpen={openPersistedProject} />

      <ReportDialog
        open={reportOpen}
        project={project}
        result={result}
        sections={capabilities.data?.report_sections ?? []}
        onClose={() => setReportOpen(false)}
      />

      <WorkspaceShell
        leftWidth={layout.widths.left}
        rightWidth={layout.widths.right}
        leftVisible={layout.visible.left}
        rightVisible={layout.visible.right}
        onLeftWidthChange={layout.setLeftWidth}
        onRightWidthChange={layout.setRightWidth}
        left={
          <InputPanel
            project={project}
            activeModule={activeModule}
            activeModuleId={resolvedActiveModuleId}
            designMethods={capabilities.data?.design_methods ?? []}
            nationalDatasets={nationalDatasets.data?.datasets ?? []}
            layers={layers}
            drawingDisplaySettings={drawingDisplaySettings}
            catalogue={catalogue}
            onProjectChange={updateProject}
            onModuleChange={updateModule}
            onActiveModuleChange={selectRoofFromInput}
            onAddModule={addRoofModule}
            onDeleteModule={deleteRoofModule}
            onEditGeometry={() => {
              setVisualScope('roof');
              setVisualTab('plan');
            }}
            onLayerToggle={(id, visible) => setLayers((previous) => ({ ...previous, [id]: visible }))}
            onDrawingDisplaySettingsChange={setDrawingDisplaySettings}
            onCatalogueChange={updateCatalogue}
          />
        }
        centre={(
          <CentreWorkspace
            project={project}
            result={visualSelection.result}
            messages={messages}
            visualStateStale={visualSelection.stale}
            layers={layers}
            drawingDisplaySettings={drawingDisplaySettings}
            geometry={geometry}
            roofHeightMillimetres={activeModule?.roof_height_above_ground?.millimetres ?? DEFAULT_ROOF_HEIGHT_MM}
            theme={theme}
            scope={visualScope}
            activeTab={visualTab}
            activeModuleId={resolvedActiveModuleId}
            selectedZoneId={selectedZoneId}
            onScopeChange={setVisualScope}
            onTabChange={setVisualTab}
            onActiveModuleChange={selectActiveModule}
            onOpenModule={openRoofWorkspace}
            onProjectChange={updateProject}
            onZoneSelect={setSelectedZoneId}
            onGeometryChange={updateGeometry}
            onLayerToggle={(id, visible) => setLayers((previous) => ({ ...previous, [id]: visible }))}
          />
        )}
        right={
          <ResultPanel
            result={result}
            isPending={calculationIsUpdating}
            error={calculationError}
            activeModuleId={resolvedActiveModuleId}
            selectedZoneId={selectedZoneId}
            onZoneSelect={setSelectedZoneId}
            onOpenVisual={(view) => {
              setVisualScope('roof');
              setVisualTab(view);
            }}
          />
        }
      />
    </div>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);

  return debounced;
}

function autoCalculationLabel({
  blocked,
  updating,
  hasResult,
  hasError,
}: {
  blocked: boolean;
  updating: boolean;
  hasResult: boolean;
  hasError: boolean;
}): string {
  if (blocked) return 'Auto: Blocked';
  if (updating) return 'Auto: Updating';
  if (hasError) return 'Auto: Needs input';
  if (hasResult) return 'Auto: Up to date';
  return 'Auto: Waiting';
}

function connectionLabel(query: { isPending: boolean; isError: boolean }): string {
  if (query.isPending) return 'Connecting';
  if (query.isError) return 'Offline';
  return 'Connected';
}
